'use client';

import React, { useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faPencil, faUserGroup } from '@fortawesome/pro-solid-svg-icons';
import { Button, Typography } from 'src/components';
import { PageHeader } from 'src/components/Settings/components';
import { useNotification } from 'src/context/notification';
import { DeleteTeamModal, CreateUpdateTeamModal } from './components';
import TeamDetailView from './components/TeamDetailView/TeamDetailView';
import { useGetAllTeams } from 'src/hooks/teams/useGetAllTeams';

const TeamManagement = () => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [teamToEdit, setTeamToEdit] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { data: allTeams } = useGetAllTeams();

  const { setNotification } = useNotification();

  const handleTeamClick = (team) => {
    setSelectedTeam(team);
  };

  const handleBackToTeams = () => {
    setSelectedTeam(null);
  };

  const handleOpenDeleteModal = (team) => {
    setTeamToDelete(team);
  };

  const handleCloseDeleteModal = () => {
    setTeamToDelete(null);
  };

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleOpenEditModal = (team) => {
    setTeamToEdit(team);
  };

  const handleCloseEditModal = () => {
    setTeamToEdit(null);
  };

  return (
    <div className="relative flex flex-col gap-6">
      <PageHeader className="bg-sky-50">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <h1 className="text-xl font-semibold text-neutral-700">Teams</h1>
            <Typography className="text-sm text-neutral">
              Manage teams and control data access permissions
            </Typography>
          </div>
          {!selectedTeam && (
            <div className="flex items-center gap-2">
              <Button
                variant="solid"
                color="neutral"
                startDecorator={<Icon icon={faPlus} />}
                onClick={handleOpenCreateModal}
                data-testid="create-team-button"
              >
                Create Team
              </Button>
            </div>
          )}
        </div>
      </PageHeader>

      {selectedTeam ? (
        <TeamDetailView team={selectedTeam} onBack={handleBackToTeams} />
      ) : (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex flex-col gap-4">
            {!allTeams?.results?.length ? (
              <div className="flex items-center justify-center h-full py-10">
                <Typography className="text-sm text-neutral">
                  No teams configured, add a team to get started.
                </Typography>
              </div>
            ) : (
              allTeams?.results?.map((team) => (
                <div
                  key={team.id}
                  className="border border-neutral-200 rounded-lg overflow-hidden shadow-sm hover:shadow transition-shadow duration-200 cursor-pointer"
                  onClick={() => handleTeamClick(team)}
                  data-testid={`team-card-${team.id}`}
                >
                  <div className="p-4 bg-white">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <Typography className="text-md font-medium text-neutral-700">
                          {team.name}
                        </Typography>
                        <div className="flex items-center bg-neutral-100 rounded-full px-2 py-0.5">
                          <Typography className="text-xs text-neutral-600">
                            <Icon
                              icon={faUserGroup}
                              size="xs"
                              className="mr-1 text-neutral-600"
                            />
                            {team.users?.length}
                          </Typography>
                        </div>
                      </div>
                      {team.editable && (
                        <div className="flex items-center space-x-2">
                          <button
                            className="text-sky-600 hover:text-sky-800 transition-colors p-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditModal(team);
                            }}
                            data-testid={`edit-team-button-${team.id}`}
                          >
                            <Icon
                              icon={faPencil}
                              className="h-4 w-4 text-neutral-500"
                            />
                          </button>
                          <button
                            className="p-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDeleteModal(team);
                            }}
                            data-testid={`delete-team-button-${team.id}`}
                          >
                            <Icon
                              icon={faTrash}
                              className="h-4 w-4 text-neutral-500 hover:text-red-500"
                            />
                          </button>
                        </div>
                      )}
                    </div>
                    {team.description && (
                      <Typography className="text-sm text-neutral">
                        {team.description}
                      </Typography>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {teamToDelete && (
        <DeleteTeamModal
          team={teamToDelete}
          open={!!teamToDelete}
          onClose={handleCloseDeleteModal}
          onSuccess={() => {
            setNotification({
              title: 'Success',
              message: 'Team deleted successfully',
              type: 'success',
            });
          }}
        />
      )}

      {/* Create Team Modal */}
      <CreateUpdateTeamModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSuccess={() => {
          setNotification({
            title: 'Success',
            message: 'Team created successfully',
            type: 'success',
          });
        }}
      />

      {/* Edit Team Modal */}
      {teamToEdit && (
        <CreateUpdateTeamModal
          team={teamToEdit}
          open={!!teamToEdit}
          onClose={handleCloseEditModal}
          onSuccess={() => {
            setNotification({
              title: 'Success',
              message: 'Team updated successfully',
              type: 'success',
            });
          }}
        />
      )}
    </div>
  );
};

export default TeamManagement;
